import React from 'react';
import { useSelector } from 'react-redux';
import { excludePrimitives } from '@gumtree/shared/src/util/list-of-primitives';

import DefaultFilter from '../filters/default-filter';
import BooleanFilter from '../filters/boolean-filter';
import { DEFAULT_FILTER_LABEL, NON_DEFAULT_FILTERS } from '../shared/constants';

import { FiltersContainer, StyledFilterTypeTitle } from './index.style';
import { LocationBlock } from './location-block/location-block';
import type { SrpState } from '../../reducers';
import { FilterItem } from '../../types';
import FrameStatic from '../filters/frame/frame-static';
import { StyledFilterBlock } from './styled-filter-block/styled-filter-block';
import RangeFilterFrame from './range-filter-frame';
import { RangeFilterFreeformFrame } from './range-filter-freeform-frame';
import Categories from './categories';
import SearchableFilter from '../filters/searchable-filter';

export const FiltersOnListView = ({
    ['data-q']: dataQ,
    className,
}: {
    ['data-q']: string;
    className?: string;
}) => {
    const {
        resultsPage: { filtersDictionary, filtersOrder, currentFilterStates },
    }: SrpState = useSelector((data: SrpState): SrpState => data);

    const booleanFilterKeys = filtersOrder.filter(
        (filterKey) => filtersDictionary[filterKey]?.type === 'boolean'
    );
    const petsHealthFilterKeys = filtersOrder.filter(
        (filterKey) => filtersDictionary[filterKey]?.type === 'boolean-pets-health'
    );
    const remainingFilterKeys = excludePrimitives(filtersOrder, [
        ...booleanFilterKeys,
        ...petsHealthFilterKeys,
    ]);

    return (
        <FiltersContainer className={className} data-q={dataQ}>
            <StyledFilterTypeTitle>Location</StyledFilterTypeTitle>
            <LocationBlock />

            <StyledFilterBlock key="categories-filter">
                <Categories />
            </StyledFilterBlock>

            {remainingFilterKeys.map((filterKey) => {
                const values = filtersDictionary[filterKey]?.values;

                if (
                    NON_DEFAULT_FILTERS.includes(filterKey) ||
                    (values.length < 2 && values[0]?.label === DEFAULT_FILTER_LABEL)
                ) {
                    return null;
                }

                if (filtersDictionary[filterKey]?.type === 'range_freeform') {
                    return (
                        <StyledFilterBlock key="price-filter">
                            <RangeFilterFreeformFrame
                                filterKey={filterKey}
                                filtersDictionary={filtersDictionary}
                                currentFilterStates={currentFilterStates}
                            />
                        </StyledFilterBlock>
                    );
                }

                if (filtersDictionary[filterKey]?.type === 'enum_range') {
                    return (
                        <StyledFilterBlock key={filterKey}>
                            <RangeFilterFrame
                                filterKey={filterKey}
                                filtersDictionary={filtersDictionary}
                                currentFilterStates={currentFilterStates}
                            />
                        </StyledFilterBlock>
                    );
                }

                const filterItemsRaw = filtersDictionary[filterKey]?.values || ([] as FilterItem[]);
                const selectedFilterValue = currentFilterStates[filterKey];

                if (filtersDictionary[filterKey]?.type === 'searchable-enum') {
                    return (
                        <StyledFilterBlock key={filterKey}>
                            <FrameStatic title={filtersDictionary[filterKey].label ?? ''}>
                                <SearchableFilter
                                    filterKey={filterKey}
                                    filterItemsRaw={filterItemsRaw}
                                    selectedFilterValue={selectedFilterValue}
                                    isStateful={false}
                                />
                            </FrameStatic>
                        </StyledFilterBlock>
                    );
                }

                return (
                    <StyledFilterBlock key={filterKey}>
                        <FrameStatic title={filtersDictionary[filterKey].label ?? ''}>
                            <DefaultFilter
                                filterKey={filterKey}
                                shortListLength={6}
                                filterItemsRaw={filterItemsRaw}
                                selectedFilterValue={selectedFilterValue}
                                isStateful={false}
                            />
                        </FrameStatic>
                    </StyledFilterBlock>
                );
            })}

            {booleanFilterKeys.length > 0 && (
                <StyledFilterBlock>
                    <FrameStatic title="Other options">
                        <BooleanFilter
                            currentFilterStates={currentFilterStates}
                            filtersDictionary={filtersDictionary}
                            filterKeys={booleanFilterKeys}
                        />
                    </FrameStatic>
                </StyledFilterBlock>
            )}

            {petsHealthFilterKeys.length > 0 && (
                <StyledFilterBlock>
                    <FrameStatic title="Health and documentation">
                        <BooleanFilter
                            currentFilterStates={currentFilterStates}
                            filtersDictionary={filtersDictionary}
                            filterKeys={petsHealthFilterKeys}
                        />
                    </FrameStatic>
                </StyledFilterBlock>
            )}
        </FiltersContainer>
    );
};
